import { useEffect, useState } from "react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "./ui/select";
import api from "../lib/api";
import { toast } from "sonner";

const SourceFormDialog = ({ open, onOpenChange, source, onSaved }) => {
    const isEdit = !!source;
    const [name, setName] = useState("");
    const [baseUrl, setBaseUrl] = useState("");
    const [extraUrls, setExtraUrls] = useState("");
    const [category, setCategory] = useState("portal");
    const [note, setNote] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (source) {
            setName(source.name || "");
            setBaseUrl(source.base_url || "");
            setExtraUrls((source.extra_urls || []).join("\n"));
            setCategory(source.category || "portal");
            setNote(source.note || "");
        } else {
            setName("");
            setBaseUrl("");
            setExtraUrls("");
            setCategory("portal");
            setNote("");
        }
        // React useState setters are referentially stable; listed only to
        // satisfy the exhaustive-deps lint rule.
    }, [source, open, setName, setBaseUrl, setExtraUrls, setCategory, setNote]);

    const parsedExtraUrls = extraUrls
        .split(/\r?\n/)
        .map((u) => u.trim())
        .filter(Boolean);

    const valid =
        name.trim().length >= 2 &&
        /^https?:\/\/.+/.test(baseUrl.trim()) &&
        parsedExtraUrls.every((u) => /^https?:\/\/.+/.test(u));

    const submit = async () => {
        if (!valid) return;
        setSaving(true);
        try {
            const payload = {
                name: name.trim(),
                base_url: baseUrl.trim(),
                extra_urls: parsedExtraUrls,
                category,
                note: note.trim(),
            };
            let saved;
            if (isEdit) {
                const { data } = await api.put(`/sources/${source.id}`, payload);
                saved = data;
                toast.success("Source updated", { description: data.name });
            } else {
                const { data } = await api.post("/sources", payload);
                saved = data;
                toast.success("Source added", {
                    description:
                        "Click Scan to probe the URL. The system will report HTTP status, response size, and 'no parser implemented yet' until a parser is wired.",
                });
            }
            onSaved?.(saved);
            onOpenChange(false);
        } catch (e) {
            toast.error(isEdit ? "Update failed" : "Could not add source", {
                description: e?.response?.data?.detail || String(e),
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg" data-testid="source-form-dialog">
                <DialogHeader>
                    <DialogTitle>
                        {isEdit ? "Edit source" : "Add new source"}
                    </DialogTitle>
                    <DialogDescription>
                        {isEdit
                            ? "Update the name, URL, category or note. The Source will not be re-scanned automatically."
                            : "Add a custom listing portal or agency website. The system will probe the URL on each scan and report HTTP status honestly — no fake data."}
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-2">
                    <Field label="Name">
                        <Input
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="e.g. Dukley Properties Montenegro"
                            data-testid="source-form-name"
                        />
                    </Field>
                    <Field label="Base URL">
                        <Input
                            type="url"
                            value={baseUrl}
                            onChange={(e) => setBaseUrl(e.target.value)}
                            placeholder="https://www.example.me/properties"
                            data-testid="source-form-url"
                        />
                        {!valid && baseUrl && (
                            <p className="text-[11px] text-destructive mt-1">
                                URL must start with http:// or https://
                            </p>
                        )}
                    </Field>
                    <Field label="Extra entry URLs (optional, one per line)">
                        <Textarea
                            rows={3}
                            value={extraUrls}
                            onChange={(e) => setExtraUrls(e.target.value)}
                            placeholder={"https://example.me/property-type/villa/\nhttps://example.me/property-type/apartment/"}
                            data-testid="source-form-extra-urls"
                            className="font-mono text-[12px]"
                        />
                        <p className="text-[11px] text-muted-foreground mt-1">
                            Each line is an additional overview URL the scraper will sweep
                            (e.g. per-type archives, region pages). Pagination is detected
                            automatically per URL.
                        </p>
                    </Field>
                    <Field label="Category">
                        <Select value={category} onValueChange={setCategory}>
                            <SelectTrigger data-testid="source-form-category">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="agency">Agency</SelectItem>
                                <SelectItem value="portal">Portal</SelectItem>
                                <SelectItem value="developer">Developer</SelectItem>
                                <SelectItem value="other">Other</SelectItem>
                            </SelectContent>
                        </Select>
                    </Field>
                    <Field label="Note (optional)">
                        <Textarea
                            rows={3}
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            placeholder="Internal reference — e.g. how this source was discovered, what segment it covers."
                            data-testid="source-form-note"
                        />
                    </Field>
                </div>
                <DialogFooter>
                    <Button
                        variant="outline"
                        onClick={() => onOpenChange(false)}
                        disabled={saving}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={submit}
                        disabled={!valid || saving}
                        data-testid="source-form-submit"
                    >
                        {saving ? "Saving…" : isEdit ? "Save changes" : "Add source"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

const Field = ({ label, children }) => (
    <div className="space-y-1.5">
        <Label className="text-[11px] uppercase tracking-[0.14em] text-muted-foreground font-medium">
            {label}
        </Label>
        {children}
    </div>
);

export default SourceFormDialog;
